import type RulesRepository from "@/core/administration/repositories/RulesRepository";
import type {
  Rule,
  RuleResponse,
  RulesFilters,
} from "@/core/administration/entities/Rule";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import type Exception from "@/core/exceptions/Exception";

const seedRules: Rule[] = [
  {
    id: "rule-p1",
    name: "P1 - Gestionnaire de relations",
    description: "Profil moindre privilège",
    status: "active",
    permissions: [
      { domain: "CUSTOMERS", action: "READ", resource: "CUSTOMER_LIST" },
      { domain: "CUSTOMERS", action: "READ", resource: "CUSTOMER_PROFILE" },
      { domain: "ONBOARDING", action: "READ", resource: "CASE" },
      { domain: "SCREENING", action: "READ", resource: "SCREENING_RESULTS" },
    ],
  },
  {
    id: "rule-p2",
    name: "P2 - Responsable données",
    description: "READ/WRITE customer data",
    status: "active",
    permissions: [
      { domain: "CUSTOMERS", action: "READ", resource: "CUSTOMER_PROFILE" },
      { domain: "CUSTOMERS", action: "WRITE", resource: "CUSTOMER_PROFILE" },
      { domain: "ONBOARDING", action: "READ", resource: "*" },
    ],
  },
  {
    id: "rule-p10",
    name: "P10 - CRM Admin",
    description: "Admin complet",
    status: "active",
    permissions: [
      { domain: "RBAC", action: "WRITE", resource: "ROLES" },
      { domain: "RBAC", action: "WRITE", resource: "USERS" },
      { domain: "ADMIN", action: "*", resource: "*" },
    ],
  },
];

export default class FakeRulesRepositoryBackend implements RulesRepository {
  private rules: Rule[] = [...seedRules];

  async getRules(
    filters: RulesFilters,
  ): Promise<RepositoryResult<RuleResponse>> {
    await new Promise((r) => setTimeout(r, 300));
    const filtered = this.rules.filter((r) => {
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (
          !r.name.toLowerCase().includes(q) &&
          !(r.description ?? "").toLowerCase().includes(q)
        )
          return false;
      }
      if (filters.status && r.status !== filters.status) return false;
      return true;
    });
    const page = filters.page ?? 0;
    const size = filters.size ?? 20;
    const items = filtered.slice(page * size, page * size + size);
    return RepositoryResultFactory.success({
      code: "00",
      message: "OK",
      data: { items, totalElements: filtered.length },
    });
  }

  async getRuleById(id: string): Promise<RepositoryResult<Rule>> {
    await new Promise((r) => setTimeout(r, 200));
    const rule = this.rules.find((r) => r.id === id);
    if (!rule) {
      const exc: Exception<null, DefaultExceptionTypes> = {
        type: DefaultExceptionTypes.NOT_FOUND,
        message: `Rule ${id} not found`,
        body: null,
      };
      return RepositoryResultFactory.error<Rule>(exc);
    }
    return RepositoryResultFactory.success(rule);
  }

  async createRule(rule: Omit<Rule, "id">) {
    await new Promise((r) => setTimeout(r, 300));
    const created: Rule = { ...rule, id: crypto.randomUUID() };
    this.rules.push(created);
    return RepositoryResultFactory.success(created);
  }

  async updateRule(
    id: string,
    rule: Omit<Rule, "id">,
  ): Promise<RepositoryResult<Rule>> {
    await new Promise((r) => setTimeout(r, 300));
    const idx = this.rules.findIndex((r) => r.id === id);
    if (idx < 0) {
      const exc: Exception<null, DefaultExceptionTypes> = {
        type: DefaultExceptionTypes.NOT_FOUND,
        message: `Rule ${id} not found`,
        body: null,
      };
      return RepositoryResultFactory.error<Rule>(exc);
    }
    this.rules[idx] = { ...rule, id };
    return RepositoryResultFactory.success(this.rules[idx]);
  }

  async deleteRule(id: string): Promise<RepositoryResult<void>> {
    await new Promise((r) => setTimeout(r, 300));
    const idx = this.rules.findIndex((r) => r.id === id);
    if (idx < 0) {
      const exc: Exception<null, DefaultExceptionTypes> = {
        type: DefaultExceptionTypes.NOT_FOUND,
        message: `Rule ${id} not found`,
        body: null,
      };
      return RepositoryResultFactory.error<void>(exc);
    }
    this.rules.splice(idx, 1);
    return RepositoryResultFactory.success(undefined);
  }
}
